import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { FormSections, Input, Spinner, TextArea, useLocalizeMessage } from 'libs.nucleus';
import { useDataViews } from '../../hooks';
export const CreateViewForm = ({ name, description, onFormChange, onValidationChange }) => {
    const translate = useLocalizeMessage();
    const { checkViewNameUnique } = useDataViews();
    const [nameError, setNameError] = useState('');
    const [isValidatingName, setIsValidatingName] = useState(false);
    // Create a debounced function to validate the name
    const validateName = useDebouncedCallback(async (nameValue) => {
        if (!nameValue.trim()) {
            setNameError(translate('Name is required'));
            setIsValidatingName(false);
            onValidationChange(false);
            return;
        }
        setIsValidatingName(true);
        try {
            const isUnique = await checkViewNameUnique(nameValue);
            if (!isUnique) {
                setNameError(translate('A dataset with this name already exists'));
                onValidationChange(false);
            }
            else {
                setNameError('');
                onValidationChange(true);
            }
        }
        catch (error) {
            // If validation fails, allow user to proceed (error will be caught on submit)
            setNameError('');
            onValidationChange(true);
        }
        finally {
            setIsValidatingName(false);
        }
    }, 500);
    // Trigger validation when name changes
    useEffect(() => {
        if (name) {
            validateName(name);
        }
        else {
            setNameError('');
            onValidationChange(false);
        }
    }, [name, validateName, onValidationChange]);
    const handleNameChange = useCallback((event) => {
        const newName = event.target.value;
        setNameError('');
        onFormChange(newName, description);
    }, [description, onFormChange]);
    const handleDescriptionChange = useCallback((event) => {
        onFormChange(name, event.target.value);
    }, [name, onFormChange]);
    const formSections = [
        {
            title: translate('Details'),
            content: (_jsxs("div", { className: 'flex flex-col gap-6 max-w-input-lg', children: [_jsxs("div", { className: 'relative', children: [_jsx(Input, { id: 'name', label: translate('Name'), value: name, onChange: handleNameChange, required: true, width: 'full', containLabels: true, hasError: !!nameError, errorMessage: nameError, autoComplete: 'off' }), isValidatingName && (_jsx("div", { className: 'absolute end-0 top-0', children: _jsx(Spinner, { size: 'sm' }) }))] }), _jsx(TextArea, { id: 'description', label: translate('Description'), value: description, onChange: handleDescriptionChange, width: 'full' })] })),
        },
    ];
    return (_jsx("div", { className: 'size-full', children: _jsx(FormSections, { sections: formSections }) }));
};
